import { useId, useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { ROW_SELECTOR } from '../grid-primitive/grid-primitive-root.js';
import { useDebounce } from '../hooks/use-debounce.js';
import { useInteractionHandler, SKIP_RECORDING } from '../hooks/use-interaction-handler.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { accept } from '../utils/event.js';
import { mergeProps } from '../utils/merge-props.js';
import { isStandardShortcutKeyPressed, isAnyModifierPressed } from '../utils/modifiers.js';
import { apple } from '../utils/platform.js';
import { OPTION_QUERY, CELL_QUERY, COLUMN_HEADER_QUERY } from '../utils/query.js';
import { getListboxId, getGridId } from './autocomplete-primitive.utils.js';

const SCROLL_OPTIONS = {
    behavior: 'instant',
    block: 'nearest',
};
const DEFAULT_OPTIONS = {
    arrowKeyBehavior: 'absolute',
    selected: undefined,
};
/** Debounce the initial focus call by N milliseconds. @default 50 */
const FOCUS_DEBOUNCE_MS = 50;
/**
 * If we can’t focus on an item after N tries, give up.
 * @default: 10
 *
 * This is just a safeguard to prevent locking the main thread in case of heavy load.
 */
const FOCUS_MAX_RETRIES = 10;
/** Create a new manager for a Autocomplete */
function useAutocomplete(userConfig) {
    const autoPopupId = useId();
    const config = {
        ...DEFAULT_OPTIONS,
        ...userConfig,
    };
    // memoization helpers: break out individual values
    const { arrowKeyBehavior, expandOnArrowDown = true, expandOnFocus = true, selected, onSelect, onActiveValueChange, popupId = autoPopupId, recordingKey, wrapArrowKeyNavigation, } = config;
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    /**
     * This state handles the scenario where a user has their pointer over the
     * listbox, but starts using arrow keys. In that scenario where a user has
     * indicated they are using keyboard navigation, if they touch their pointer
     * again, their current selection is lost. Instead, we set a “soft lock” that
     * ignores pointer events until some trigger resets it (usually if they move
     * their pointer off the component entirely and interact with something else).
     */
    const [inputMode, setInputMode] = useState('pointer');
    // value-element mappings. We need this to support generic value types.
    const elementToDataRef = useRef(new WeakMap());
    const valueToElementRef = useRef(new Map());
    const registerOption = useCallback((el, value) => {
        if (el) {
            elementToDataRef.current.set(el, value);
            valueToElementRef.current.set(value.value, new WeakRef(el));
        }
        return () => {
            if (el)
                elementToDataRef.current.delete(el);
            valueToElementRef.current.delete(value.value);
        };
    }, []);
    const [expandedUncontrolled, setExpandedUncontrolled] = useState(config.expanded ?? false);
    const expanded = typeof config.expanded === 'boolean' ? config.expanded : expandedUncontrolled;
    const onExpand = useCallback((shouldBeExpanded) => {
        config.onExpand?.(shouldBeExpanded);
        setExpandedUncontrolled(shouldBeExpanded);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config not memoizable
    [config.onExpand, setExpandedUncontrolled]);
    const [activeValueUncontrolled, setActiveValueUncontrolled] = useState(undefined);
    // Always use the controlled value if it is provided (even if it is undefined),
    // otherwise use the uncontrolled value
    const userDefinedActiveValue = 'activeValue' in config;
    const activeValue = userDefinedActiveValue ? config.activeValue : activeValueUncontrolled;
    const setActiveValue = useCallback((next) => {
        setInputMode('pointer'); // on selecting an option, whether click or Enter, reset input mode
        onActiveValueChange?.(next);
        if (!userDefinedActiveValue)
            setActiveValueUncontrolled(next);
    }, [onActiveValueChange, userDefinedActiveValue]);
    const onSelectCustomRef = useRef();
    // Internal check: the currently-active value exists, but is disabled
    const [activeValueDisabled, setActiveValueDisabled] = useState(false);
    const firstItem = useCallback(() => {
        // note: first visible may not be detectable, in which case always fall back to first absolute
        const firstVisible = arrowKeyBehavior === 'viewport'
            ? getFirstVisibleOption(elementToDataRef.current, scrollRef.current)
            : undefined;
        const activeOption = firstVisible || getFirstOption(elementToDataRef.current, scrollRef.current);
        setActiveValue(activeOption?.value);
        onSelectCustomRef.current = activeOption?.onSelect;
    }, [arrowKeyBehavior, setActiveValue]);
    const lastItem = useCallback(() => {
        const lastOption = getLastOption(elementToDataRef.current, scrollRef.current);
        setActiveValue(lastOption?.value);
        onSelectCustomRef.current = lastOption?.onSelect;
    }, [setActiveValue]);
    const nextItem = useCallback((event) => {
        if (!scrollRef.current)
            return;
        if (activeValue) {
            // note: we have to query for all children, because the next one may not be a direct sibling in the DOM
            const options = scrollRef.current.querySelectorAll(OPTION_QUERY) || [];
            let optionFound = false;
            for (let i = 0; i < options.length; ++i) {
                const option = options[i];
                if (isDisabled(option)) {
                    setActiveValueDisabled(true);
                }
                else {
                    setActiveValueDisabled(false);
                }
                const activeOption = elementToDataRef.current.get(option);
                if (activeOption?.value === activeValue) {
                    if (i === options.length - 1) {
                        // We've hit the end, so loop to the first value if the event isn't a repeat
                        if (event?.repeat) {
                            return;
                        }
                        if (wrapArrowKeyNavigation !== false) {
                            firstItem();
                        }
                        return;
                    }
                    optionFound = true;
                    continue;
                }
                if (optionFound) {
                    option.scrollIntoView(SCROLL_OPTIONS);
                    setActiveValue(activeOption?.value);
                    onSelectCustomRef.current = activeOption?.onSelect;
                    return;
                }
            }
        }
        // otherwise, get first item
        firstItem();
    }, [activeValue, firstItem, setActiveValue, wrapArrowKeyNavigation]);
    const prevItem = useCallback((event) => {
        if (!scrollRef.current)
            return;
        if (activeValue) {
            const options = scrollRef.current.querySelectorAll(OPTION_QUERY) || [];
            let optionFound = false;
            for (let i = options.length - 1; i >= 0; --i) {
                const option = options[i];
                if (isDisabled(option)) {
                    setActiveValueDisabled(true);
                }
                else {
                    setActiveValueDisabled(false);
                }
                const activeOption = elementToDataRef.current.get(option);
                if (activeOption?.value === activeValue) {
                    if (i === 0) {
                        // We've hit the end, so loop to the first value if the event isn't a repeat
                        if (event?.repeat) {
                            return;
                        }
                        if (wrapArrowKeyNavigation !== false) {
                            lastItem();
                        }
                        return;
                    }
                    optionFound = true;
                    continue;
                }
                if (optionFound) {
                    option.scrollIntoView(SCROLL_OPTIONS);
                    setActiveValue(activeOption?.value);
                    onSelectCustomRef.current = activeOption?.onSelect;
                    return;
                }
            }
        }
        // otherwise, get first item
        firstItem();
    }, [activeValue, firstItem, lastItem, setActiveValue, wrapArrowKeyNavigation]);
    // if expanded and nothing is active, focus on first item (but don’t re-render too much)
    const hasScrolledToActiveValueRef = useRef(false);
    const callCount = useRef(0);
    // If the activeValue changes, reset the hasScrolledToActiveValueRef and callCount.
    // This allows the container to scroll to the new active value.
    useEffect(() => {
        hasScrolledToActiveValueRef.current = false;
        callCount.current = 0;
    }, [activeValue]);
    const shouldFocusFirst = expanded && !config.skipAutoSetFirstItemActive && !activeValue && !userDefinedActiveValue;
    const debouncedFirstItem = useDebounce(() => {
        firstItem();
        ++callCount.current;
    }, { timeout: FOCUS_DEBOUNCE_MS, leading: true }, [firstItem]);
    // if expanded and user has defined the activeValue, attempt to scroll to it.
    // It is not guaranteed that the option child has been rendered yet
    // (but don’t re-render too much, try up to the maximum)
    const shouldScrollToActiveValue = expanded && userDefinedActiveValue && activeValue && !hasScrolledToActiveValueRef.current;
    const debouncedScrollToActiveValue = useDebounce((value) => {
        const elementRef = valueToElementRef.current.get(value);
        const element = elementRef?.deref();
        if (element) {
            element.scrollIntoView(SCROLL_OPTIONS);
            hasScrolledToActiveValueRef.current = true;
        }
        ++callCount.current;
    }, { timeout: FOCUS_DEBOUNCE_MS, leading: true }, []);
    if (expanded && callCount.current < FOCUS_MAX_RETRIES) {
        if (shouldFocusFirst) {
            debouncedFirstItem();
        }
        else if (shouldScrollToActiveValue) {
            debouncedScrollToActiveValue(activeValue);
        }
    }
    else if (!expanded) {
        callCount.current = 0;
        hasScrolledToActiveValueRef.current = false;
    }
    // lastly, queue up one retry to focus the first item after expanding
    useLayoutEffect(() => {
        if (shouldFocusFirst)
            firstItem();
    }, [shouldFocusFirst, firstItem]);
    const [userInputRef, setUserInputRef] = useState();
    const mergedInputRef = useMergeRefs(inputRef, userInputRef);
    const inputOnFocus = useCallback(() => {
        if (!expandOnFocus)
            return;
        onExpand(true);
    }, [expandOnFocus, onExpand]);
    const inputOnBlur = useCallback((e) => {
        if (!expandOnFocus)
            return;
        if (scrollRef.current &&
            // we want to check for any element inside the floating-ui-portal associated with the current list
            e.relatedTarget?.closest('[data-floating-ui-portal]')?.contains(scrollRef.current)) {
            // if the element that just got focus is part of our popup, just refocus the input
            const refocus = () => {
                inputRef?.current?.focus();
                document.removeEventListener('pointerup', refocus);
                document.removeEventListener('pointercancel', refocus);
            };
            // defer refocus until pointerup so we don't interrupt onclick/onselect
            document.addEventListener('pointerup', refocus, { once: true });
            document.addEventListener('pointercancel', refocus, { once: true });
        }
        else {
            // otherwise, close the popup
            onExpand(false);
        }
    }, [expandOnFocus, onExpand]);
    const inputOnKeyDown = useInteractionHandler((event) => {
        // Note: break = handled; return = not handled
        switch (event.code) {
            case 'Home': {
                if (!expanded || isAnyModifierPressed(event))
                    return; // ignore collapsed and modifiers
                firstItem();
                break;
            }
            case 'End': {
                if (!expanded || isAnyModifierPressed(event))
                    return; // ignore collapsed and modifiers
                lastItem();
                break;
            }
            case 'Enter':
            case 'NumpadEnter': {
                if (expanded &&
                    !isAnyModifierPressed(event) &&
                    activeValue !== undefined &&
                    !activeValueDisabled) {
                    onSelect?.(activeValue, { event });
                    onSelectCustomRef.current?.(activeValue);
                    break;
                }
                return;
            }
            case 'Escape': {
                if (!expanded || isAnyModifierPressed(event))
                    return; // ignore collapsed and modifiers
                onExpand(false);
                break;
            }
            case 'Space': {
                if (expanded)
                    return; // Space ONLY expands if collapsed, otherwise we don’t handle event.
                onExpand(true);
                break;
            }
            case 'ArrowDown': {
                const isCmdDown = isStandardShortcutKeyPressed(event);
                // if expandOnArrowDown === false, don’t expand
                if (!expanded && !expandOnArrowDown && !isCmdDown) {
                    return SKIP_RECORDING; // don’t handle event
                }
                if (!expanded) {
                    onExpand(true);
                    break;
                }
                // ignore non-standard shortcut key modifiers
                else if (!isCmdDown && isAnyModifierPressed(event)) {
                    return SKIP_RECORDING; // don’t handle event
                }
                nextItem(event);
                break;
            }
            case 'KeyN': {
                if (!apple || !event.ctrlKey || isStandardShortcutKeyPressed(event)) {
                    return SKIP_RECORDING;
                }
                nextItem(event);
                break;
            }
            case 'KeyP':
            case 'ArrowUp': {
                if (event.code === 'KeyP' && (!apple || !event.ctrlKey))
                    return SKIP_RECORDING; // don’t handle event
                // ignore attempts to scroll while listbox isn't visible
                if (!expanded && !expandOnArrowDown) {
                    return SKIP_RECORDING;
                }
                // return to top on ⌘ + ↑
                else if (isStandardShortcutKeyPressed(event)) {
                    firstItem();
                }
                // ignore non-standard shortcut key modifiers on ArrowUp only (e.g. Shift+↑), symmetric with ArrowDown.
                // Ctrl + KeyP must still reach prevItem(); do not treat as pass-through.
                else if (event.code === 'ArrowUp' &&
                    !isStandardShortcutKeyPressed(event) &&
                    isAnyModifierPressed(event)) {
                    return SKIP_RECORDING; // don’t handle event
                }
                else {
                    prevItem(event);
                }
                break;
            }
            default: {
                return SKIP_RECORDING; // don’t handle event
            }
        }
        // event was accepted
        accept(event);
        setInputMode('keyboard');
    }, { eventName: 'keydown', recordingKey }, [
        activeValueDisabled,
        expanded,
        onExpand,
        firstItem,
        lastItem,
        nextItem,
        prevItem,
        setActiveValue,
    ]);
    // @ts-expect-error ref types are incompatible
    const getInputProps = useCallback((props) => {
        // Note: this is normally bad practice, as this could lead to infinite
        // re-renders.  But we need to do it here because it is the user that
        // ultimately controls the <input>, and we need to preserve their ref
        // while having our own.
        if (props && 'ref' in props && props.ref !== userInputRef) {
            setUserInputRef(props.ref);
        }
        return mergeProps({
            ref: mergedInputRef,
            role: 'combobox',
            'aria-expanded': expanded,
            'aria-controls': getListboxId(popupId),
            'aria-activedescendant': activeValue
                ? valueToElementRef.current.get(activeValue)?.deref()?.id
                : undefined,
            'aria-autocomplete': 'list',
            'aria-haspopup': 'listbox',
            onFocus: inputOnFocus,
            onBlur: inputOnBlur,
            onKeyDown: inputOnKeyDown,
        }, props ?? {});
    }, [
        activeValue,
        expanded,
        mergedInputRef,
        inputOnBlur,
        inputOnFocus,
        inputOnKeyDown,
        popupId,
        userInputRef,
    ]);
    const getListProps = useCallback((props) => mergeProps({
        registerOption,
        activeValue,
        id: popupId,
        inputMode,
        setActiveValue,
        expanded,
        onExpand,
        onSelect,
        refs: { input: inputRef, scroll: scrollRef },
        selected: selected ?? [],
        setInputMode,
    }, props ?? {}), [
        activeValue,
        expanded,
        inputMode,
        onExpand,
        onSelect,
        popupId,
        registerOption,
        selected,
        setActiveValue,
        setInputMode,
    ]);
    const triggerOnClick = useCallback((ev) => {
        onExpand(!expanded);
        accept(ev);
    }, [expanded, onExpand]);
    const getTriggerProps = useCallback((propsIn) => mergeProps(propsIn ?? {}, {
        onClick: triggerOnClick,
        'aria-controls': getListboxId(popupId),
        'aria-expanded': expanded,
        // TODO: break out dialog hook
        'aria-haspopup': 'listbox',
    }), [expanded, popupId, triggerOnClick]);
    const dialogOnClose = useCallback(() => {
        onExpand(false);
    }, [onExpand]);
    const getDialogProps = useCallback((propsIn) => mergeProps(propsIn ?? {}, {
        onClose: dialogOnClose,
    }), [dialogOnClose]);
    return useMemo(() => ({
        activeValue,
        config: Object.freeze(config),
        getDialogProps,
        getInputProps,
        getListProps,
        getTriggerProps,
        firstItem,
        lastItem,
        nextItem,
        prevItem,
        refs: {
            input: inputRef,
            scroll: scrollRef,
        },
        registerOption,
        selected: selected ?? [],
        setActiveValue,
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config not memoizable
    [
        activeValue,
        getDialogProps,
        getInputProps,
        getListProps,
        getTriggerProps,
        firstItem,
        lastItem,
        nextItem,
        prevItem,
        registerOption,
        selected,
        setActiveValue,
    ]);
}
function useAutocompleteGrid(userConfig) {
    const autoPopupId = useId();
    const config = userConfig ?? {};
    // memoization helpers: break out individual values
    const { expandOnArrowDown = true, expandOnFocus = true, popupId = autoPopupId, recordingKey, } = config;
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const gridRef = useRef(null);
    const [inputMode, setInputMode] = useState('pointer');
    const gridNav = useAutocompleteGridNavigation(gridRef);
    const [expandedUncontrolled, setExpandedUncontrolled] = useState(config.expanded ?? false);
    const expanded = typeof config.expanded === 'boolean' ? config.expanded : expandedUncontrolled;
    const onExpand = useCallback((shouldBeExpanded) => {
        config.onExpand?.(shouldBeExpanded);
        setExpandedUncontrolled(shouldBeExpanded);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config not memoizable
    [config.onExpand, setExpandedUncontrolled]);
    const [userInputRef, setUserInputRef] = useState();
    const mergedInputRef = useMergeRefs(inputRef, userInputRef);
    const inputOnFocus = useCallback(() => {
        if (!expandOnFocus)
            return;
        onExpand(true);
    }, [expandOnFocus, onExpand]);
    const inputOnBlur = useCallback(() => {
        if (!expandOnFocus)
            return;
        onExpand(false);
    }, [expandOnFocus, onExpand]);
    // if the popup is expanded, but nothing is selected yet, focus on first item
    const shouldFocusFirst = expanded && !gridNav.activeId;
    const callCount = useRef(0);
    const debouncedFirstCell = useDebounce(() => {
        gridNav.firstCell();
        ++callCount.current;
    }, { timeout: FOCUS_DEBOUNCE_MS, leading: true }, [gridNav.firstCell]);
    if (shouldFocusFirst && callCount.current < FOCUS_MAX_RETRIES) {
        debouncedFirstCell();
    }
    else if (!expanded) {
        callCount.current = 0;
    }
    // lastly, queue up one focus attempt
    useLayoutEffect(() => {
        if (shouldFocusFirst)
            gridNav.firstCell();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- gridNav not memoizable
    }, [shouldFocusFirst, gridNav.firstCell]);
    const inputOnKeyDown = useInteractionHandler((event) => {
        // Note: break = handled; return = not handled
        switch (event.key) {
            case 'Enter':
            case 'Space': {
                if (!expanded) {
                    onExpand(true);
                }
                else if (gridNav.activeId) {
                    cells.current.get(gridNav.activeId)?.onSelect?.();
                }
                else {
                    return SKIP_RECORDING; // don’t handle event
                }
                break;
            }
            case 'ArrowDown': {
                const isCmdDown = isStandardShortcutKeyPressed(event);
                if (!expanded && !expandOnArrowDown && !isCmdDown) {
                    return SKIP_RECORDING;
                }
                if (!expanded) {
                    onExpand(true);
                    break;
                }
                else if (!isCmdDown && isAnyModifierPressed(event)) {
                    return SKIP_RECORDING; // don’t handle non-shortcut key modifiers
                }
                gridNav.nextRow();
                break;
            }
            case 'ArrowUp': {
                if (!expanded || isAnyModifierPressed(event))
                    return; // ignore collapsed and modifiers
                gridNav.prevRow();
                break;
            }
            case 'ArrowLeft': {
                if (!expanded || inputRef.current?.value !== '')
                    return; // don’t handle if not expanded or if there's text in the input
                gridNav.prevCell();
                break;
            }
            case 'ArrowRight': {
                if (!expanded || inputRef.current?.value !== '')
                    return; // don’t handle if not expanded or if there's text in the input
                gridNav.nextCell();
                break;
            }
            case 'Home': {
                if (!expanded || isAnyModifierPressed(event))
                    return; // ignore collapsed and modifiers
                gridNav.startOfRow();
                break;
            }
            case 'End': {
                if (!expanded || isAnyModifierPressed(event))
                    return; // ignore collapsed and modifiers
                gridNav.endOfRow();
                break;
            }
            default: {
                return SKIP_RECORDING; // don’t handle other events
            }
        }
        accept(event);
        setInputMode('keyboard');
    }, { eventName: 'keydown', recordingKey }, [gridNav.activeId, gridNav, expanded, onExpand]);
    // @ts-expect-error ref types are incompatible
    const getInputProps = useCallback((props) => {
        // Note: this is normally bad practice, as this could lead to infinite
        // re-renders.  But we need to do it here because it is the user that
        // ultimately controls the <input>, and we need to preserve their ref
        // while having our own.
        if (props && 'ref' in props && props.ref !== userInputRef) {
            setUserInputRef(props.ref);
        }
        return mergeProps({
            ref: mergedInputRef,
            role: 'combobox',
            'aria-expanded': expanded,
            'aria-controls': getGridId(popupId),
            'aria-activedescendant': gridNav.activeId,
            'aria-haspopup': 'grid',
            onFocus: inputOnFocus,
            onBlur: inputOnBlur,
            onKeyDown: inputOnKeyDown,
        }, props ?? {});
    }, [
        gridNav.activeId,
        expanded,
        mergedInputRef,
        inputOnBlur,
        inputOnFocus,
        inputOnKeyDown,
        popupId,
        userInputRef,
    ]);
    /**
     * Keep an internal record of all cells registered, so that the onSelect()
     * callback can be fired on <kbd>Enter</kbd>. registerCell() adds itself
     * to the list on mount, and removes itself on unmount, ensuring memory
     * isn’t too bad. Further, we bypass memoization for onSelect using Refs
     * (in Autocomplete.Grid.Cell), so we’re not firing stale callbacks.
     */
    const cells = useRef(new Map());
    const registerCell = useCallback((props) => {
        const stableId = props.id; // create static reference, just in edge case it changes between init and teardown
        cells.current.set(stableId, props);
        return () => cells.current.delete(stableId);
    }, []);
    // @ts-expect-error ref types are incompatible
    const getGridProps = useCallback((props) => mergeProps({
        activeId: gridNav.activeId,
        activeRowId: gridNav.activeRowId,
        id: getGridId(popupId),
        inputMode,
        setInputMode,
        registerCell,
        ref: gridRef,
        refs: {
            input: inputRef,
            grid: gridRef,
            scroll: scrollRef,
        },
        setActiveId: gridNav.setActiveId,
    }, props ?? {}), [
        gridNav.activeId,
        gridNav.activeRowId,
        inputMode,
        setInputMode,
        popupId,
        registerCell,
        gridNav.setActiveId,
    ]);
    const triggerOnClick = useCallback((ev) => {
        onExpand(!expanded);
        accept(ev);
    }, [expanded, onExpand]);
    const getTriggerProps = useCallback((propsIn) => mergeProps(propsIn ?? {}, {
        onClick: triggerOnClick,
        'aria-controls': getListboxId(popupId),
        'aria-expanded': expanded,
        // TODO: break out dialog hook
        'aria-haspopup': 'listbox',
    }), [expanded, popupId, triggerOnClick]);
    return useMemo(() => ({
        activeId: gridNav.activeId,
        activeRowId: gridNav.activeRowId,
        config: Object.freeze(config),
        endOfRow: gridNav.endOfRow,
        firstCell: gridNav.firstCell,
        getGridProps,
        getInputProps,
        getTriggerProps,
        lastCell: gridNav.lastCell,
        nextCell: gridNav.nextCell,
        nextRow: gridNav.nextRow,
        prevCell: gridNav.prevCell,
        prevRow: gridNav.prevRow,
        registerCell,
        refs: {
            input: inputRef,
            grid: gridRef,
            scroll: scrollRef,
        },
        setActiveId: gridNav.setActiveId,
        startOfRow: gridNav.startOfRow,
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config not memoizable
    [getGridProps, getInputProps, getTriggerProps, gridNav, registerCell]);
}
/** Get the first visible option within a container. */
function getFirstVisibleOption(elementToData, scrollEl) {
    if (!scrollEl)
        return undefined;
    const { scrollTop } = scrollEl;
    // Note: these elements may not be siblings, they may be grouped,
    // there may be custom wrappers, etc. Query for all in DOM, then find first
    // one to match.
    const options = scrollEl.querySelectorAll(OPTION_QUERY);
    for (const option of options) {
        // ⚠️ DON’T use getBoundingClientRect()! Though it’s accurate, every
        // single call will trigger reflow, which is expensive.
        // Further, using IntersectionObserver here would require attaching observers
        // on every option, and keeping more state in memory that could lead to
        // re-renders. All that to say, IntersectionObserver is usually the right choice!
        // Just not here.
        if (option.offsetTop + option.offsetHeight >= scrollTop) {
            return elementToData.get(option);
        }
    }
    return undefined;
}
/** Get the first option in a list */
function getFirstOption(elementToData, scrollEl) {
    if (!scrollEl)
        return undefined;
    scrollEl.scrollTo({ top: 0 });
    const option = scrollEl?.querySelector(OPTION_QUERY);
    return option ? elementToData.get(option) : undefined;
}
/** Get the last option in a list */
function getLastOption(elementToData, scrollEl) {
    if (!scrollEl)
        return undefined;
    scrollEl.scrollTo({ top: scrollEl.offsetHeight });
    const options = scrollEl?.querySelectorAll(OPTION_QUERY);
    return elementToData.get(options[options.length - 1]) ?? undefined;
}
function isDisabled(el) {
    return ['', 'true'].includes(el.getAttribute('aria-disabled'));
}
/**
 * Navigate along a grid.

 * @note This makes heavy use of querySelectors, rather than DOM traversal, in
 * order to handle scenarios where elements have wrappers and containers. We
 * don’t enforce a strict structure here.
 */
function useAutocompleteGridNavigation(gridRef) {
    const [activeId, setActiveId] = useState();
    const activeRowId = useMemo(() => {
        const cell = activeId && document.getElementById(activeId);
        if (!cell) {
            return;
        }
        cell.scrollIntoView(SCROLL_OPTIONS);
        return getParentRow(cell)?.id;
    }, [activeId]);
    const firstCell = useCallback(() => {
        if (!gridRef.current) {
            return;
        }
        const cell = gridRef.current.querySelector(CELL_QUERY);
        if (cell) {
            setActiveId(cell.id);
        }
    }, [gridRef]);
    const lastCell = useCallback(() => {
        if (!gridRef.current) {
            return;
        }
        const cells = gridRef.current.querySelectorAll(CELL_QUERY);
        if (cells[cells.length - 1]) {
            setActiveId(cells[cells.length - 1].id);
        }
    }, [gridRef]);
    const moveCell = useCallback((dir) => {
        const activeCell = document.getElementById(activeId);
        const parentRow = getParentRow(activeCell);
        if (!parentRow) {
            firstCell();
            return;
        }
        const cells = parentRow.querySelectorAll(CELL_QUERY);
        if (!cells.length) {
            return;
        }
        // start/end: get either end
        if (dir === 'rowStart' && cells[0]) {
            setActiveId(cells[0].id);
            return;
        }
        if (dir === 'rowEnd' && cells[cells.length - 1]) {
            setActiveId(cells[cells.length - 1].id);
            return;
        }
        // prev/next: first find current position, then move forward or back 1
        const colNo = getElIndex(cells, activeCell);
        const nextCell = cells[colNo + (dir === 'prev' ? -1 : 1)];
        if (nextCell) {
            setActiveId(nextCell.id);
        }
    }, [activeId, firstCell]);
    const moveRow = useCallback((dir) => {
        const activeCell = document.getElementById(activeId);
        const parentRow = getParentRow(activeCell);
        if (!parentRow || !gridRef.current) {
            firstCell();
            return;
        }
        // Get current column position
        const colNo = getElIndex(parentRow.querySelectorAll(CELL_QUERY), activeCell);
        // Iterate through all rows (remember—they may not be DOM siblings)
        const rows = gridRef.current.querySelectorAll(ROW_SELECTOR);
        const rowNo = getElIndex(rows, parentRow);
        let currentRow = rowNo;
        let hasCells = false;
        while (!hasCells) {
            currentRow += dir === 'prev' ? -1 : 1;
            const nextRow = rows[currentRow];
            if (!nextRow) {
                break;
            }
            // keep going until we find a row that has cells, without column headers
            const nextCells = nextRow.querySelectorAll(CELL_QUERY);
            if (nextCells.length && !nextRow.querySelector(COLUMN_HEADER_QUERY)) {
                hasCells = true;
                // Note: clamp to number of cells in row, just in case this row has fewer (which isn’t ideal but we can’t catch it in all cases)
                const clampedColNo = Math.min(colNo, nextCells.length - 1);
                setActiveId(nextCells[clampedColNo].id);
                break;
            }
        }
    }, [activeId, firstCell, gridRef]);
    return useMemo(() => ({
        activeId,
        activeRowId,
        endOfRow: () => moveCell('rowEnd'),
        firstCell,
        lastCell,
        nextCell: () => moveCell('next'),
        nextRow: () => moveRow('next'),
        prevCell: () => moveCell('prev'),
        prevRow: () => moveRow('prev'),
        setActiveId,
        startOfRow: () => moveCell('rowStart'),
    }), [activeId, activeRowId, firstCell, lastCell, moveCell, moveRow, setActiveId]);
}
/** Get the cell’s parent row */
function getParentRow(cell) {
    return cell?.parentElement?.closest(ROW_SELECTOR) || undefined;
}
/** Get the index of an element in a node list */
function getElIndex(nodes, el) {
    for (let i = 0; i < nodes.length; ++i) {
        if (nodes[i] === el)
            return i;
    }
    return -1;
}

export { SCROLL_OPTIONS, useAutocomplete, useAutocompleteGrid };
//# sourceMappingURL=autocomplete-primitive.hooks.js.map
